import { useEffect, useState } from "react";
import { DASHBOARD_NAVIGATION_OPTIONS, NAVIGATION_SECTIONS } from "../../../constants";
import NavigationItem from "../NavigationItem";
import { useSelector, useDispatch } from "react-redux";
import useScreen from "../../../hooks/useScreen";
import { BiChevronDown, BiChevronUp } from "react-icons/bi";
import { Tooltip } from "@mui/material";
import { updateMenuDrawer } from "../../../reducer/appReducer";

const activeMenuWithHotelFilters = ["Dashboard", "Bookings", "Reports"];

// Returns the section key that contains the currently active menu item.
const getActiveSectionKey = (options, activeMenu) => {
  const match = options.find(
    (o) => o.title?.toLowerCase() === activeMenu?.toLowerCase()
  );
  return match?.section ?? null;
};

// Expanded sidebar — clickable section heading with chevron
const SectionLabel = ({ label, isOpen, onClick }) => (
  <button
    onClick={onClick}
    className="w-full flex items-center justify-between px-[20px] pt-[16px] pb-[6px] cursor-pointer group"
    data-testid="section-label-button"
  >
    <span className="text-white/80 text-[11px] font-bold uppercase tracking-[0.12em] font-outfit group-hover:text-white transition-colors">
      {label}
    </span>
    {isOpen ? (
      <BiChevronUp className="text-white/60 text-[16px] group-hover:text-white transition-colors" />
    ) : (
      <BiChevronDown className="text-white/60 text-[16px] group-hover:text-white transition-colors" />
    )}
  </button>
);

// Collapsed sidebar — one icon per section, tooltip shows section label
const CollapsedSectionIcon = ({ section, isActive, onClick }) => (
  <Tooltip title={section.label} placement="right">
    <button
      onClick={onClick}
      data-testid={`collapsed-section-icon-${section.key}`}
      className={`w-full flex items-center justify-center py-[10px] cursor-pointer transition-colors ${
        isActive ? "bg-[#00000026]" : "hover:bg-[#00000026]"
      }`}
    >
      <img src={section.icon} alt={section.label} className="w-[22px] h-[22px]" />
    </button>
  </Tooltip>
);

const NavigationBarList = ({ dashboardSelectHandler, activePage, posName }) => {
  const dispatch = useDispatch();

  const [
    DASHBOARD_NAVIGATION_OPTIONS_MODIFIED,
    SET_DASHBOARD_NAVIGATION_OPTIONS_MODIFIED,
  ] = useState([]);

  // openSections tracks which section keys are expanded (expanded sidebar only).
  const [openSections, setOpenSections] = useState({});

  const {
    viewBookingAllowed,
    viewClientAllowed,
    viewRatedAndAvailabiltyAllowed,
    editHouseKeepingAllowed,
    createPOSAllowed,
    currentUserPosIds,
    viewLedgerAllowed,
    viewPaymentAllowed,
    viewExpenseAllowed,
    viewInvoiceAllowed,
    isOwner,
    viewStocksAllowed,
    editStocksAllowed,
  } = useSelector((store) => store.usersRestriction);

  const { isMobileScreen } = useSelector((store) => store.appHeader);
  const { isAllHotelsSelected } = useSelector((store) => store.login);
  const { activePosSlug } = useSelector((store) => store.posSetting);
  const { hotelDetails } = useSelector((store) => store.login);
  const { menuDrawer, activeMenu } = useSelector((store) => store.app);

  const screen = useScreen();

  // Build the filtered options list
  useEffect(() => {
    const filtered = DASHBOARD_NAVIGATION_OPTIONS?.filter((option) => {
      let isAllowed = false;

      if (option?.title === "Bookings" && viewBookingAllowed) {
        option.link =
          hotelDetails?.bookingView === "calender"
            ? "bookingReservation"
            : "bookings";
        isAllowed = true;
      }

      if (option?.title === "Channel Manager" && viewRatedAndAvailabiltyAllowed)
        isAllowed = true;

      if (option?.title === "Clients" && viewClientAllowed) isAllowed = true;
      if (option?.title === "Housekeeping" && editHouseKeepingAllowed)
        isAllowed = true;

      if (
        (option?.title === "POS" &&
          Array?.isArray(currentUserPosIds) &&
          currentUserPosIds?.length > 0) ||
        isOwner
      )
        isAllowed = true;

      if (option?.title === "Ledger" && viewLedgerAllowed) isAllowed = true;
      if (option?.title === "Payments" && viewPaymentAllowed) isAllowed = true;
      if (option?.title === "Expense" && viewExpenseAllowed) isAllowed = true;
      if (option?.title === "Invoice" && viewInvoiceAllowed) isAllowed = true;

      if (
        option?.title === "Manage Stocks" &&
        (viewStocksAllowed || editStocksAllowed)
      )
        isAllowed = true;

      if (
        (option?.title === "Expense" ||
          option?.title === "Invoice" ||
          option?.title === "Payments") &&
        isMobileScreen
      )
        isAllowed = false;

      if (option?.title === "Dashboard" || option?.title === "Reports")
        isAllowed = true;

      if (option?.title === "Booking Engine") isAllowed = true;

      return isAllowed;
    }).filter((option) =>
      isAllHotelsSelected
        ? activeMenuWithHotelFilters.includes(option.title)
        : true
    );

    SET_DASHBOARD_NAVIGATION_OPTIONS_MODIFIED(filtered);

    // Open the section containing the active page; close all others.
    const activeSectionKey = getActiveSectionKey(filtered, activeMenu);
    const initial = {};
    NAVIGATION_SECTIONS.forEach((s) => {
      initial[s.key] = s.key === activeSectionKey;
    });
    setOpenSections(initial);
  }, [
    DASHBOARD_NAVIGATION_OPTIONS,
    isAllHotelsSelected,
    createPOSAllowed,
    currentUserPosIds,
    isOwner,
    screen,
    hotelDetails,
    activeMenu,
  ]);

  const toggleSection = (key) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Clicking a section icon in collapsed mode: expand the sidebar and open
  // that section (close all others for a clean focused view).
  const handleCollapsedSectionClick = (key) => {
    const next = {};
    NAVIGATION_SECTIONS.forEach((s) => { next[s.key] = s.key === key; });
    setOpenSections(next);
    dispatch(updateMenuDrawer(false));
  };

  const activeSectionKey = getActiveSectionKey(
    DASHBOARD_NAVIGATION_OPTIONS_MODIFIED,
    activeMenu
  );

  // ── Collapsed sidebar ───────────────────────────────────────────────────────
  if (menuDrawer) {
    return (
      <div className="flex flex-col pt-[65px]" data-testid="collapsed-nav">
        {NAVIGATION_SECTIONS.map((section) => {
          const hasItems = DASHBOARD_NAVIGATION_OPTIONS_MODIFIED.some(
            (o) => o.section === section.key
          );
          if (!hasItems) return null;

          return (
            <div key={section.key} data-testid={`nav-section-${section.key}`}>
              <CollapsedSectionIcon
                section={section}
                isActive={activeSectionKey === section.key}
                onClick={() => handleCollapsedSectionClick(section.key)}
              />
            </div>
          );
        })}
      </div>
    );
  }

  // ── Expanded sidebar ────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col pt-[65px]">
      {NAVIGATION_SECTIONS.map((section) => {
        const sectionItems = DASHBOARD_NAVIGATION_OPTIONS_MODIFIED.filter(
          (option) => option.section === section.key
        );

        if (sectionItems.length === 0) return null;

        const isOpen = openSections[section.key];

        return (
          <div key={section.key} data-testid={`nav-section-${section.key}`}>
            <SectionLabel
              label={section.label}
              isOpen={isOpen}
              onClick={() => toggleSection(section.key)}
            />

            {isOpen && (
              <div data-testid={`nav-section-items-${section.key}`}>
                {sectionItems.map((option) => (
                  <NavigationItem
                    key={option.id}
                    navigationOptions={option}
                    dashboardSelectHandler={dashboardSelectHandler}
                    activePosSlug={activePosSlug}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default NavigationBarList;
